import { type VcsApi } from '@env0/vcs-service/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useApiClient from 'hooks/use-api-client';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { useCurrentProjectId } from 'hooks/path-params-extraction.hooks';
import { useHasPermission } from 'hooks/use-has-permission';

export const useGetVcsConnections = () => {
  const { isAuthorized: canManageVcsConnections } = useHasPermission('MANAGE_VCS_CONNECTIONS');
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'vcs', 'connections'],
    queryFn: () => apiClient.vcsConnections.listVcsConnections(orgId!),
    enabled: canManageVcsConnections && Boolean(orgId)
  });
};

export const useFindVcsConnection = (
  enabled: boolean,
  vcsType: VcsApi.VcsType,
  connectionType: VcsApi.ConnectionType,
  repositoryUrl?: string
) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'vcs', 'connections', vcsType, connectionType, repositoryUrl],
    queryFn: () => apiClient.vcsConnections.findVcsConnection(orgId!, vcsType, connectionType, repositoryUrl!),
    enabled: Boolean(repositoryUrl) && enabled
  });
};

export const useGetVcsRepositories = (vcsType: string, connectionType: VcsApi.ConnectionType) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'vcs', 'repositories', vcsType, connectionType],
    queryFn: () => apiClient.vcsConnections.getVcsRepositories(orgId!, vcsType, connectionType),
    enabled: !!vcsType,
    staleTime: Infinity
  });
};

export const useGetVcsRepositoriesById = (vcsConnectionId: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'vcs', 'repositories', vcsConnectionId],
    queryFn: () => apiClient.vcsConnections.getVcsRepositoriesById(orgId!, vcsConnectionId),
    enabled: !!vcsConnectionId
  });
};

export const useCreateVcsConnection = () => {
  const orgId = useCurrentOrganizationId();
  const projectId = useCurrentProjectId();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vcsConnection: Omit<VcsApi.CreateVcsConnection.Request.Body, 'organizationId'>) =>
      apiClient.vcsConnections.createVcsConnection({ ...vcsConnection, organizationId: orgId, projectId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [orgId, 'vcs'] });
    }
  });
};

export const useUpdateVcsConnection = () => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: VcsApi.UpdateVcsConnection.Request.Body }) =>
      apiClient.vcsConnections.updateVcsConnection(id, data),
    onSuccess: () => () => queryClient.invalidateQueries({ queryKey: [orgId, 'vcs'] })
  });
};

export const useDeleteVcsConnection = () => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => apiClient.vcsConnections.deleteVcsConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [orgId, 'vcs'] });
    }
  });
};

export const useGetVcsConnectionHealth = (vcsConnectionId: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'vcs', 'health', vcsConnectionId],
    queryFn: () => apiClient.vcsConnections.getVcsConnectionHealth(vcsConnectionId),
    enabled: !!vcsConnectionId
  });
};
